import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageSeo } from "@/hooks/usePageSeo";
import { categories } from "@/mocks/categories";
import { siteIntro } from "@/mocks/site";

const usageRules = [
  "本站所有作品简介、评分、播放量等数据均为产品演示用示例，不代表任何真实片方信息。",
  "详情页播放区域仅展示封面与界面示意，不提供视频流、下载或外链播放能力。",
  "筛选、排序、搜索与分页均在前端完成，数据不会上传到服务器。",
  "收藏功能基于浏览器本地存储，清除浏览器数据后将一并清除，不涉及账号体系。",
];

const copyrightPoints = [
  "站内所有文字内容与页面设计为演示原型，仅供产品展示与内部评审使用。",
  "演示封面图由 AI 生成，不含任何真实影视剧照或人物肖像。",
  "如需上线运营，请确保所有内容均取得合法授权，并遵守当地内容监管规定。",
  "本站面向 18 岁以上用户，请理性浏览，未成年人请勿访问。",
];

export default function AboutPage() {
  usePageSeo({
    title: "激情视频 - 关于我们与使用说明",
    description:
      "了解激情视频的平台定位、内容范围与使用说明，包括分类体系、收藏机制、演示数据说明与版权声明。",
    keywords: "激情视频,关于我们,视频平台介绍,使用说明,版权声明",
  });

  return (
    <SiteLayout>
      <div className="px-4 md:px-8 lg:px-12 pt-5 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      <section className="px-4 md:px-8 lg:px-12 mt-4">
        <div className="relative rounded-lg overflow-hidden border border-background-200/70">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20cinematic%20banner%20with%20deep%20crimson%20and%20charcoal%20black%20gradient%20smoke%2C%20soft%20film%20grain%20texture%2C%20subtle%20amber%20light%20streaks%2C%20elegant%20minimal%20dark%20atmosphere%2C%20editorial%20art%20composition&width=1920&height=600&seq=passion-about-banner&orientation=landscape"
              alt="激情视频平台介绍主视觉"
              title="激情视频 精选激情影视与短视频平台"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/40" />
          <div className="relative px-6 py-12 md:px-10 md:py-16">
            <div className="flex items-center gap-2.5">
              <span className="h-[2px] w-8 bg-primary-500" />
              <span className="text-[11px] tracking-[0.28em] text-accent-400 font-label uppercase">About Us</span>
            </div>
            <h1 className="mt-3 font-heading text-4xl md:text-5xl font-bold text-foreground-50 leading-tight">
              关于{siteIntro.title.replace("关于", "")}
            </h1>
            <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-foreground-300 max-w-2xl">
              {siteIntro.summary}
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 mt-10 md:mt-14">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
          {siteIntro.points.map((point) => (
            <div key={point.title} className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <span className="w-10 h-10 rounded-md bg-primary-500/12 text-primary-400 flex items-center justify-center">
                <i className={`${point.icon} text-lg`} />
              </span>
              <h2 className="mt-3 text-sm font-medium text-foreground-50">{point.title}</h2>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{point.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 mt-10 md:mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <div className="flex items-center gap-2.5">
              <span className="h-[2px] w-6 bg-primary-500" />
              <span className="text-[11px] tracking-[0.24em] text-accent-400 font-label uppercase">Content Scope</span>
            </div>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-50">内容范围</h2>
            <p className="mt-3 text-sm leading-[1.9] text-foreground-400">
              平台内容以两类核心形态呈现：90 分钟以上的剧情长片，以及 5-15 分钟的精品短视频。
              两类内容共用同一套标签体系，你可以按地区、年份、时长自由组合筛选，也可以从主题标签进入相关合集。
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="group flex items-start gap-3 p-3.5 rounded-md bg-background-200/40 border border-background-300/40 hover:border-primary-500/50 transition-colors cursor-pointer"
                >
                  <span className="w-8 h-8 rounded-sm bg-primary-500/15 text-primary-400 flex items-center justify-center shrink-0">
                    <i className={`${category.icon}`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-foreground-100 group-hover:text-accent-400 transition-colors">
                      {category.name}
                    </span>
                    <span className="block mt-1 text-[11px] leading-relaxed text-foreground-500">{category.description}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-50">联系方式</h2>
            <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
              内容合作、版权问题或使用建议，欢迎通过以下方式联系。
            </p>
            <ul className="mt-5 space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-sm bg-secondary-800/70 text-accent-400 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-[11px] text-foreground-600">合作邮箱</p>
                  <p className="text-xs text-foreground-200 mt-0.5">contact@passion-video.demo</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-sm bg-secondary-800/70 text-accent-400 flex items-center justify-center shrink-0">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-[11px] text-foreground-600">服务时间</p>
                  <p className="text-xs text-foreground-200 mt-0.5">工作日 10:00 - 19:00</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-sm bg-secondary-800/70 text-accent-400 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line" />
                </span>
                <div>
                  <p className="text-[11px] text-foreground-600">年龄限制</p>
                  <p className="text-xs text-foreground-200 mt-0.5">仅限 18 岁以上用户浏览</p>
                </div>
              </li>
            </ul>
            <Link
              to="/search"
              className="mt-6 flex items-center justify-center gap-1.5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line" />
              从搜索开始浏览
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 mt-10 md:mt-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-50 flex items-center gap-2">
              <i className="ri-book-open-line text-accent-400 text-xl" />
              使用说明
            </h2>
            <ul className="mt-4 space-y-3">
              {usageRules.map((rule, index) => (
                <li key={rule} className="flex items-start gap-2.5">
                  <span className="font-label text-xs text-primary-400 mt-0.5 w-4 shrink-0">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs leading-relaxed text-foreground-400">{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-50 flex items-center gap-2">
              <i className="ri-copyright-line text-accent-400 text-xl" />
              版权声明
            </h2>
            <ul className="mt-4 space-y-3">
              {copyrightPoints.map((point, index) => (
                <li key={point} className="flex items-start gap-2.5">
                  <span className="font-label text-xs text-primary-400 mt-0.5 w-4 shrink-0">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs leading-relaxed text-foreground-400">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 mt-10">
        <div className="rounded-lg border border-dashed border-background-300/70 bg-background-100 px-6 py-10 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
            已准备好开始挑选了吗？
          </h2>
          <p className="mt-2 text-sm text-foreground-500">从最新上线的作品开始，或直接进入热门片单。</p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <Link
              to="/"
              className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-home-5-line" />
              返回首页
            </Link>
            <Link
              to="/category/theme"
              className="h-10 px-5 rounded-md border border-background-300/70 text-sm text-foreground-200 hover:border-primary-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-fire-line" />
              热门主题
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}